/**
 * CountdownMentionChip - React component for rendering @countdown mentions in tiptap editor
 *
 * Displays countdown mentions as styled chips with the @timerName format.
 * Orange/amber color scheme to distinguish from blue link chips.
 * Follows LinkMentionChip pattern exactly.
 */

import React, { memo } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { Box, Typography } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';

const chipSx = {
  backgroundColor: '#FFF3E0',
  color: '#E65100',
  padding: '2px 8px',
  borderRadius: '12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontWeight: 500,
  cursor: 'default',
  userSelect: 'none' as const,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  '&:hover': {
    backgroundColor: '#FFE0B2',
  },
};

const iconSx = {
  fontSize: '0.875rem',
  opacity: 0.7,
};

const tagMarkerSx = {
  color: '#666',
  fontSize: '0.75em',
  fontFamily: 'monospace',
  verticalAlign: 'baseline',
};

export const CountdownMentionChip = memo(function CountdownMentionChip({
  node,
}: NodeViewProps) {
  const { id, labelTag } = node.attrs;

  return (
    <NodeViewWrapper
      as="span"
      className="countdown-mention-wrapper"
      contentEditable={false}
      draggable
      style={{
        display: 'inline-block',
        margin: '0 2px',
        verticalAlign: 'baseline',
      }}
    >
      {labelTag && (
        <Box component="span" sx={tagMarkerSx}>{`<${labelTag}>`}</Box>
      )}
      <Box
        component="span"
        className="countdown-mention-chip"
        data-countdown-id={id}
        sx={chipSx}
      >
        <TimerIcon sx={iconSx} />
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 'inherit', fontSize: 'inherit' }}
        >
          @{id}
        </Typography>
      </Box>
      {labelTag && (
        <Box component="span" sx={tagMarkerSx}>{`</${labelTag}>`}</Box>
      )}
    </NodeViewWrapper>
  );
});

export default CountdownMentionChip;
